import { object, ref, string, type InferType } from "yup";
import { isUSA } from "../../../../common/utils";
import { AddressInputSchemaGenerator } from "../../../SharedAddressInput/constants";
import { getNameFromCountry } from "../../mappers";
import { stripBsbDash } from "./utils";

export enum BecsPaymentPrefInputFields {
  AccountName = "accountName",
  BsbCode = "bsbCode",
  BankName = "bankName",
  AccountNumber = "accountNumber",
  ConfirmAccountNumber = "confirmAccountNumber",
  AdditionalInstructions = "additionalInstructions",
  Address = "address",
  BankCountryValidatedCountry = "bankCountryValidatedCountry",
}

export const ADDITIONAL_INSTRUCTIONS_MAX_LENGTH = 18;
export const BSB_DIGITS = 6;

export const BecsPaymentPrefInputSchema = object()
  .shape({
    [BecsPaymentPrefInputFields.AccountName]: string()
      .required("Required field")
      .matches(/\S/, "Account name cannot be just spaces"),
    [BecsPaymentPrefInputFields.BsbCode]: string()
      .required("Required field")
      .test("bsb-digits", `BSB code must be ${BSB_DIGITS} digits`, (value) => {
        if (!value) return false;
        return stripBsbDash(value).length === BSB_DIGITS;
      })
      .test("bsb-numeric", "BSB code must contain only digits", (value) => {
        if (!value) return false;
        return /^[0-9]+$/.test(stripBsbDash(value));
      }),
    [BecsPaymentPrefInputFields.BankName]: string().required("Required field"),
    [BecsPaymentPrefInputFields.AccountNumber]: string()
      .required("Required field")
      .matches(/^\d{5,9}$/, "Account number must be 5-9 digits"),
    [BecsPaymentPrefInputFields.ConfirmAccountNumber]: string()
      .oneOf([ref(BecsPaymentPrefInputFields.AccountNumber)], "Must match account number")
      .required("Required field"),
    [BecsPaymentPrefInputFields.AdditionalInstructions]: string().max(
      ADDITIONAL_INSTRUCTIONS_MAX_LENGTH,
      `Must be ${ADDITIONAL_INSTRUCTIONS_MAX_LENGTH} characters or less`,
    ),
    [BecsPaymentPrefInputFields.BankCountryValidatedCountry]: string().optional(),
  })
  .concat(
    object().shape({
      [BecsPaymentPrefInputFields.Address]: AddressInputSchemaGenerator({
        testIsUSA: (country) => isUSA(getNameFromCountry(country)),
        zipRequiredForNonUS: false,
      }),
    }),
  );

export type BecsPaymentPrefInputType = InferType<typeof BecsPaymentPrefInputSchema>;
